import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Minus, Plus } from "lucide-react";
import { SERVICES } from "../data/content";
import { MaskedLines, Reveal, EASE } from "../components/Motion";
import { BookingButton, InquiryButton } from "../components/Cta";

function Faq({ faqs, testid }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="faq-list" data-testid={testid}>
      {faqs.map((faq, i) => (
        <div className={`faq-item ${open === i ? "open" : ""}`} key={i}>
          <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`${testid}-question-${i + 1}`} aria-expanded={open === i}>
            <span>{faq.q}</span>
            {open === i ? <Minus size={16} /> : <Plus size={16} />}
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                className="faq-answer"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                <p>{faq.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const index = SERVICES.findIndex((s) => s.slug === slug);
  if (index === -1) return <Navigate to="/services" replace />;
  const service = SERVICES[index];
  const prev = SERVICES[(index - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(index + 1) % SERVICES.length];

  return (
    <>
      <section className="page-hero" data-testid="service-hero">
        <div className="ph-image" style={{ backgroundImage: `url(${service.heroImage})` }} />
        <div className="ph-overlay" />
        <div className="ph-content">
          <p className="crumb">
            <Link to="/services" data-testid="service-crumb-services">Services</Link> <span>/</span> {service.name}
          </p>
          <h1>
            <MaskedLines lines={[service.name]} delay={0.2} />
          </h1>
          <p className="ph-tagline">{service.tagline}</p>
          <p className="ph-intro">{service.intro}</p>
          <div className="ph-tags">
            <span className="tag gold" data-testid="service-stat">
              {service.stat.value} {service.stat.label}
            </span>
            <span className="tag">Est. 2014</span>
            <span className="tag">In-house team</span>
          </div>
        </div>
      </section>

      <section className="deliverables section-pad" data-testid="service-deliverables">
        <Reveal className="section-heading">
          <p className="eyebrow">What&apos;s included</p>
          <span className="section-count">01 — 0{service.deliverables.length}</span>
        </Reveal>
        <div>
          {service.deliverables.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.05}>
              <div className="deliver-row">
                <span className="num">0{i + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pricing section-pad" data-testid="service-pricing">
        <Reveal className="section-heading">
          <div>
            <p className="eyebrow">Packages</p>
            <h2>
              Pick a <em>starting point.</em>
            </h2>
          </div>
          <span className="section-count">Final quotes follow a short brief</span>
        </Reveal>
        <div className="pricing-grid">
          {service.packages.map((pack, i) => (
            <Reveal key={pack.name} delay={i * 0.08} className={`price-card ${pack.featured ? "featured" : ""}`}>
              {pack.featured && <span className="price-flag">Most chosen</span>}
              <h3>{pack.name}</h3>
              <p className="price">
                {pack.price}
                <span>{pack.per}</span>
              </p>
              <p className="price-blurb">{pack.blurb}</p>
              <ul>
                {pack.features.map((feature) => (
                  <li key={feature}>
                    <Check size={13} />
                    {feature}
                  </li>
                ))}
              </ul>
              <InquiryButton label={`Enquire — ${pack.name}`} testid={`price-${service.slug}-${i + 1}-button`} className={pack.featured ? "primary-button" : "outline-button"} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="service-portfolio section-pad" data-testid="service-portfolio">
        <Reveal className="section-heading">
          <div>
            <p className="eyebrow">Recent {service.name.toLowerCase()} work</p>
            <h2>
              Proof, not <em>promises.</em>
            </h2>
          </div>
          <Link to="/work" className="text-link" data-testid="service-portfolio-all-link">
            Full portfolio <ArrowRight size={15} />
          </Link>
        </Reveal>
        <div className="mini-grid">
          {service.portfolio.map((piece, i) => (
            <Reveal key={piece.title} delay={i * 0.08}>
              <Link to="/work" className="mini-card" data-testid={`service-portfolio-${i + 1}`}>
                <img src={piece.image} alt={piece.title} loading="lazy" />
                <div className="project-shade" />
                <span className="mini-tag">{piece.tag}</span>
                <h3>{piece.title}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="faq section-pad" data-testid="service-faq">
        <Reveal className="section-heading">
          <div>
            <p className="eyebrow">Questions, answered</p>
            <h2>
              Before you <em>ask.</em>
            </h2>
          </div>
        </Reveal>
        <Faq faqs={service.faqs} testid="service-faq-list" />
      </section>

      <section className="service-cta section-pad" data-testid="service-inquiry-cta">
        <Reveal>
          <p className="eyebrow">Start here</p>
          <h2>
            Bring us the
            <br />
            <em>{service.name.toLowerCase()} brief.</em>
          </h2>
          <div className="contact-actions row">
            <InquiryButton label={`Discuss ${service.name.toLowerCase()}`} testid="service-cta-inquiry-button" />
            <BookingButton testid="service-cta-booking-button" />
          </div>
        </Reveal>
      </section>

      <nav className="service-nav section-pad" data-testid="service-prev-next">
        <Link to={`/services/${prev.slug}`} className="sn-link" data-testid="service-prev-link">
          <ArrowLeft size={16} />
          <span>
            <em>Previous</em>
            {prev.name}
          </span>
        </Link>
        <Link to={`/services/${next.slug}`} className="sn-link right" data-testid="service-next-link">
          <span>
            <em>Next</em>
            {next.name}
          </span>
          <ArrowRight size={16} />
        </Link>
      </nav>
    </>
  );
}
